import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DateFieldFr } from '@/components/ui/date-field-fr';
import { HistoryEmployeeCard, fmtH } from '@/components/history/HistoryEmployeeCard';

const firstOfYear = () => `${new Date().getFullYear()}-01-01`;
const EMPTY = { search: '', start: '', end: '', site: '' };
const PAGE_SIZE = 20;

export default function DeactivatedEmployeesHistory({ user }) {
  const [filters, setFilters] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (f, p) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p), page_size: String(PAGE_SIZE) });
      if (f.search) params.set('search', f.search);
      if (f.start) params.set('start_date', f.start);
      if (f.end) params.set('end_date', f.end);
      if (f.site) params.set('site_id', f.site);
      const r = await axios.get(`${API}/employees/deactivated-history?${params}`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      toast.error(`Historique non chargé : ${typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'vérifiez la connexion'}`);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(applied, page); }, [load, applied, page]);

  const apply = (f) => { setApplied(f); setPage(1); };
  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));
  const employees = data?.employees || [];
  const t = data?.totals;
  const pages = data ? Math.max(1, Math.ceil(data.total_employees / PAGE_SIZE)) : 1;

  return (
    <Layout user={user} activeModule="b2b">
      <div className="space-y-6 p-4" data-testid="deactivated-history-page">
        <header>
          <h1 className="text-2xl font-bold text-slate-900">Historique des salariés désactivés</h1>
          <p className="text-sm text-slate-600">Prestations conservées après désactivation — consultation seule, aucun salarié n’est réactivé et aucune donnée n’est copiée.</p>
        </header>
        <form className="flex flex-wrap items-end gap-2" onSubmit={(e) => { e.preventDefault(); apply(filters); }} data-testid="history-filters">
          <Input placeholder="Nom ou e-mail" value={filters.search} onChange={(e) => set('search')(e.target.value)} className="w-56" data-testid="history-search" />
          <label className="text-xs text-slate-600">Du<DateFieldFr value={filters.start} onChange={set('start')} testid="history-start" /></label>
          <label className="text-xs text-slate-600">Au<DateFieldFr value={filters.end} onChange={set('end')} testid="history-end" /></label>
          <select value={filters.site} onChange={(e) => set('site')(e.target.value)} className="h-9 rounded border px-2 text-sm" data-testid="history-site">
            <option value="">Tous les clients / sites</option>
            {(data?.sites || []).map((s) => <option key={s.site_id} value={s.site_id}>{s.name}</option>)}
          </select>
          <Button type="submit" disabled={loading} data-testid="history-apply">{loading ? 'Chargement…' : 'Rechercher'}</Button>
          <Button type="button" variant="ghost" onClick={() => { const f = { ...EMPTY, start: firstOfYear() }; setFilters(f); apply(f); }} data-testid="history-this-year">Cette année</Button>
        </form>
        {t && (
          <p className="text-sm text-slate-700" data-testid="history-summary">
            {data.total_employees} salarié(s) désactivé(s) · {t.days} journée(s) · {t.missions} mission(s) · {t.validated} validée(s) · <b>{fmtH(t.hours)}</b>
            <span className="text-slate-500"> — totaux sur l’ensemble des résultats</span>
          </p>
        )}
        {data && employees.length === 0 && <p className="text-sm text-slate-500" data-testid="history-empty">Aucun salarié désactivé ne correspond à ces critères.</p>}
        <div className="space-y-4">{employees.map((e) => <HistoryEmployeeCard key={e.user_id} employee={e} />)}</div>
        {pages > 1 && (
          <div className="flex items-center gap-3 text-sm" data-testid="history-pagination">
            <Button size="sm" variant="outline" disabled={page <= 1 || loading} onClick={() => setPage(page - 1)} data-testid="history-prev">Précédent</Button>
            <span data-testid="history-page">Page {page} / {pages}</span>
            <Button size="sm" variant="outline" disabled={page >= pages || loading} onClick={() => setPage(page + 1)} data-testid="history-next">Suivant</Button>
          </div>
        )}
      </div>
    </Layout>
  );
}
